/*
 *   Copyright (c) 2025 LoginID Inc
 *   All rights reserved.

 *   Licensed under the Apache License, Version 2.0 (the "License");
 *   you may not use this file except in compliance with the License.
 *   You may obtain a copy of the License at

 *   http://www.apache.org/licenses/LICENSE-2.0

 *   Unless required by applicable law or agreed to in writing, software
 *   distributed under the License is distributed on an "AS IS" BASIS,
 *   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *   See the License for the specific language governing permissions and
 *   limitations under the License.
 */

import { Button, Grid, Text, Divider, Image, Container, ActionIcon, Stack } from "@mantine/core";
import {
  IconCreditCard,
  IconFaceId,
  IconFingerprint,
  IconChevronDown
} from "@tabler/icons-react";
import { LIDService } from "@/services/loginid";
import ParseUtil from "@/lib/parse";
import { CheckoutRequest } from ".";
import { useState } from "react";

export interface TxPayload {
  Merchant: string;
  Subtotal: string;
  Shipping: string;
  Tax: string;
  Total: string;
  At: Date;
  Address: Address;
  Contact: string;
  Email: string;
  Note: string;
}

export interface Address {
  Street: string;
  City: string;
  State: string;
  Country: string;
  Postal: string;
}

export interface CheckoutConfirmPromptProps {
  username: string;
  token: string;
  request: CheckoutRequest;
  hasPasskey: boolean;
  onComplete: (email: string, token: string, next: string) => void;
}

/**
 * CheckoutConfirmPrompt
 *
 * This component displays the payment summary to the user and handles passkey-based transaction confirmation.
 *
 * Responsibilities:
 * - Displays a breakdown of the order details (shipping, contact info, total).
 * - Triggers passkey-based transaction authentication with the LoginID Wallet SDK (`performAction("passkey:tx")`).
 * - Handles both success (payload signature returned) and failure (error shown).
 *
 * Flow Summary:
 * 1. Show the user a detailed confirmation page with merchant and payment info.
 * 2. When the user clicks "Confirm", initiate passkey authentication.
 * 3. On successful signing, complete the checkout flow by passing token back to the parent.
 */
export function CheckoutConfirmPrompt(props: CheckoutConfirmPromptProps) {
  const payData: TxPayload = {
    Merchant: props.request.merchant,
    Subtotal: props.request.subtotal,
    Tax: props.request.tax,
    Shipping: props.request.shipping,
    Total: props.request.total,
    At: new Date(),
    Contact: "Joe Smith",
    Email: props.username,
    Address: {
      Street: "1 Wall Street",
      City: "Newyork",
      State: "NY",
      Country: "US",
      Postal: "123456",
    },
    Note: "This payment will not be immediately reflected in your current balance.",
  };

  const brand = process.env.REACT_APP_BRAND || "Paze";
  const [error, setError] = useState("");
  const [txRef, setTxRef] = useState("");

  function ButtonIcon() {
    const SIZE = 24;
    if (props.token !== "") {
      return <IconCreditCard size={SIZE}></IconCreditCard>;
    } else {
      if (ParseUtil.isIPhone()) {
        return <IconFaceId size={SIZE}></IconFaceId>;
      } else {
        return <IconFingerprint size={SIZE}></IconFingerprint>;
      }
    }
  }

  async function confirmPayTranstion() {
    // clear prior result
    if (props.token !== "") {
      return props.onComplete(
        props.username,
        props.token,
        props.hasPasskey ? "passkey" : "none",
      );
    }

    clear();

    try {
      const result = await LIDService.client.performAction("passkey:tx", {
        txPayload: JSON.stringify(payData),
      });

      if (result.payloadSignature) {
        return props.onComplete(
          props.username,
          result.payloadSignature,
          "passkey",
        );
      }
    } catch (e: any) {
      setError(e.message || e.msg || e);
    }
  }

  async function cancelTransaction(){
        return props.onComplete(
          props.username,
          "",
          "cancel",
        );
  }

  function clear() {
    setError("");
    setTxRef("");
  }

  return (
    <Container w="100%">
      {txRef && (
        <>
          <Grid>
            <Grid.Col span={12} ta="center">
              <Text c="green.5">Transaction Successfull</Text>
            </Grid.Col>
          </Grid>
        </>
      )}
      {error && <Text c="red.5">{error}</Text>}
      <Grid gutter={0}>
        <Grid.Col span={6}>
          <Text fw={700} ta="left" size="sm">
            Payment
          </Text>
        </Grid.Col>

        <Grid.Col span={6} ta="right" >
          <ActionIcon size={24} variant="subtle" aria-label="expand" >
            <IconChevronDown size={24} color="#818BB1" />
          </ActionIcon>
        </Grid.Col>
        <Grid.Col
          span={2}
          style={{ display: "flex", justifyContent: "flex-start" }}
        >
          <Image h={36} w={64} src="/assets/my-bank.png" />
        </Grid.Col>
        <Grid.Col span={10}>
          <Text size="sm" ta="left">
            <b>My Bank Rewards</b>
            <br />
            ******6593
          </Text>
        </Grid.Col>
      </Grid>
      <Divider mb="xs" mt="xs"></Divider>
      <Grid gutter={0}>
        <Grid.Col span={6}>
          <Text fw={700} ta="left" size="sm">
            Shipping
          </Text>
        </Grid.Col>

        <Grid.Col span={6} ta="right" >
          <ActionIcon size={24} variant="subtle" aria-label="expand" >
            <IconChevronDown size={24} color="#818BB1" />
          </ActionIcon>
        </Grid.Col>
        <Grid.Col span={12} >
          <Text size="sm" ta="left">
            <b>{payData.Contact}</b>
            <br/>
            {payData.Address.Street}
            <br />
            {payData.Address.City} , {payData.Address.State} 
          </Text>
        </Grid.Col>
      </Grid>
      <Divider mb="xs" mt="xs"></Divider>
      <Grid gutter={0}>
        <Grid.Col span={12}>
          <Text fw={700} ta="left" size="sm">
            Contact
          </Text>
        </Grid.Col>
        <Grid.Col span={8}>
          <Text size="sm" ta="left">
            {payData.Contact}
            <br />
            {payData.Email} <br />
          </Text>
        </Grid.Col>
      </Grid>
      <Divider mb="xs" mt="xs"></Divider>
      <Grid gutter={0}>
        <Grid.Col span={8}>
          <Text fw={500} ta="left" size="xs">
            SUBTOTAL
          </Text>
        </Grid.Col>
        <Grid.Col span={4}>
          <Text size="xs" ta="right">
            {payData.Subtotal}{" "}
          </Text>
        </Grid.Col>
        <Grid.Col span={8}>
          <Text fw={500} ta="left" size="xs">
            SHIPPING
          </Text>
        </Grid.Col>
        <Grid.Col span={4}>
          <Text size="xs" ta="right">
            {payData.Shipping}{" "}
          </Text>
        </Grid.Col>
      </Grid>
      <Grid mt={0}>
        <Grid.Col span={8}>
          <Text fw={500} ta="left" size="xs">
            TAX
          </Text>
        </Grid.Col>
        <Grid.Col span={4}>
          <Text size="xs" ta="right">
            {payData.Tax}{" "}
          </Text>
        </Grid.Col>
      </Grid>
      <Grid>
        <Grid.Col span={8}>
          <Text fw={700} ta="left">
            PAY {payData.Merchant}{" "}
          </Text>
        </Grid.Col>
        <Grid.Col span={4} ta="right">
          ${payData.Total}
        </Grid.Col>
      </Grid>
      <Divider mb="xs" mt="xs"></Divider>
      <Text size="sm" ta="left">By continuing, your information will be shared with {payData.Merchant}. </Text>
      <Stack>
        
      <Button style={{borderRadius:16}} mt="md" justify="space-between" leftSection={<span/>} rightSection={ButtonIcon()} onClick={confirmPayTranstion}>
        Confirm details
      </Button>
      <Button variant="transparent" onClick={cancelTransaction}>Exit {brand} and return to Agent</Button>
      </Stack>
    </Container>
  );
}
